import { useNavigate, useParams } from 'react-router-dom';
import { Button, Form, Row, Space } from 'antd';

import { IProductStore, productStore } from 'containers/Products/Product/store';

import { usePatchRequest, usePostRequest, useUserRole } from 'hooks';
import { getIsNew } from 'utils';

import { ProductCreate, ProductDetails, ProductUpdate } from 'codegen';
import { RecursiveRequired } from 'types';

import { PublishProductBtn } from './PublishProductBtn';

import {
  handleProductDetailsPatchRequest,
  handleProductDetailsPostRequest,
  handleSuccessPatchProductDetails,
  handleSuccessPostProductDetails,
} from '../lib';

const { Item, useWatch, useFormInstance } = Form;

export const ProductFormActions = () => {
  const initialValues = productStore((state) => state.initialValues);

  const form = useFormInstance<RecursiveRequired<IProductStore['initialValues']>>();
  const navigate = useNavigate();
  const { productId } = useParams();

  const [postProductDetails, { loading: postLoading }] = usePostRequest<ProductDetails, ProductCreate>({
    url: 'products/details',
    options: {
      onSuccess: handleSuccessPostProductDetails(navigate),
    },
  });

  const [patchProductDetails, { loading: patchLoading }] = usePatchRequest<ProductDetails, ProductUpdate>({
    url: `products/details/${productId}`,
    options: {
      onSuccess: handleSuccessPatchProductDetails,
    },
  });

  const { isAdmin } = useUserRole();

  const isNew = getIsNew(productId);

  const name = useWatch('name', form);
  const regions = useWatch('regions', form);
  const sponsorCode = useWatch('sponsorCode', form);
  const productIdVal = useWatch('productId', form);

  const disabled = !name || !sponsorCode || !productIdVal || !regions?.length;

  if (!isAdmin) {
    return null;
  }

  return (
    <Item
      noStyle
      shouldUpdate
    >
      {() => (
        <Row
          justify='end'
          className='mt-6'
        >
          <Space.Compact>

            <PublishProductBtn disabled={disabled} />

            <Button
              onClick={() => form.resetFields()}
              disabled={!form?.isFieldsTouched() || patchLoading || postLoading}
            >
              Undo
            </Button>

            <Button
              type='primary'
              loading={patchLoading || postLoading}
              disabled={
                !form?.isFieldsTouched()
                || disabled
                || !!form.getFieldsError().some(({ errors }) => !!errors.length)
              }
              onClick={
                isNew
                  ? handleProductDetailsPostRequest(form, postProductDetails)
                  : handleProductDetailsPatchRequest(form, initialValues, patchProductDetails)
              }
            >
              Save
            </Button>
          </Space.Compact>
        </Row>
      )}
    </Item>
  );
};
